// Relative imports, not the '@' alias — see useCourseReview.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import type {ReviewPayload} from './useCourseReview';
import {
  applyResultToHistory,
  ChatContext,
  ChatMessage,
  EditResult,
  mergeEditResult,
  Revision,
  sortRevisions,
} from '../../Screens/LMS/editor/editorLogic';

/**
 * The chat-first editor's requests (spec §2):
 *
 *   POST course/<uuid>/edit/                 {page_id, ops}            -> EditResult
 *   POST course/<uuid>/undo/                 {page_id?, batch_id?}     -> EditResult
 *   GET  course/<uuid>/page/<id>/history/                              -> {revisions}
 *   GET  course/<uuid>/page/<id>/chat/                                 -> {messages}
 *   POST course/<uuid>/page/<id>/chat/       {message, context}        -> {messages, edit}
 *
 * Every EditResult is merged into the cached review in place — every
 * `['course-review', uuid, opened]` variant `useCourseReview` keeps — so the
 * screen changes without a reload or a loading state. Flags and the publish
 * gate are not part of an EditResult, so the review is then re-read quietly in
 * the background (react-query keeps showing the merged data meanwhile).
 */

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

const base = (uuid: string) => `/video_learning/course/${uuid}`;

/** The prefix every cached review variant of this course shares. */
export const reviewKey = (uuid: string) => ['course-review', uuid];
export const chatKey = (uuid: string, pageId: number) => [
  'course-edit-chat',
  uuid,
  pageId,
];
export const historyKey = (uuid: string, pageId: number) => [
  'course-edit-history',
  uuid,
  pageId,
];

/** Longer than the ~60s worst case of one chat turn. */
export const CHAT_TIMEOUT_MS = 90000;

export type EditOp = {op: string; page_id?: number; [field: string]: unknown};
export type ChatReply = {messages: ChatMessage[]; edit: EditResult | null};

/** Merge a result into every cached review and the page's history. */
export function applyEditResult(
  client: QueryClient,
  uuid: string,
  pageId: number | null | undefined,
  result: EditResult,
  source: Revision['source'],
) {
  client.setQueriesData<ReviewPayload>(reviewKey(uuid), old =>
    old ? mergeEditResult(old, result) : old,
  );
  if (pageId != null) {
    client.setQueryData<Revision[]>(historyKey(uuid, pageId), old =>
      applyResultToHistory(old, result, source),
    );
  }
  // Flags an op resolved and the gate are re-read silently; the merged review
  // stays on screen until the fresh one lands.
  client.invalidateQueries(reviewKey(uuid), {refetchType: 'active'});
}

export function useChapterChat(
  uuid: string | null,
  pageId: number | null,
): UseQueryResult<ChatMessage[], Error> {
  return useQuery<ChatMessage[], Error>(
    chatKey(uuid ?? '', pageId ?? -1),
    async () => {
      const r = await ChessRestClient.get(
        `${base(uuid as string)}/page/${pageId}/chat/`,
        {headers: await authHeader()},
      );
      return (r.data?.messages ?? []) as ChatMessage[];
    },
    {enabled: !!uuid && pageId != null, staleTime: 60 * 1000},
  );
}

export function usePageHistory(
  uuid: string | null,
  pageId: number | null,
  enabled = true,
): UseQueryResult<Revision[], Error> {
  return useQuery<Revision[], Error>(
    historyKey(uuid ?? '', pageId ?? -1),
    async () => {
      const r = await ChessRestClient.get(
        `${base(uuid as string)}/page/${pageId}/history/`,
        {headers: await authHeader()},
      );
      return sortRevisions((r.data?.revisions ?? []) as Revision[]);
    },
    {enabled: enabled && !!uuid && pageId != null, staleTime: 60 * 1000},
  );
}

export function useCourseEditor(uuid: string) {
  const client = useQueryClient();

  const edit = useMutation<
    EditResult,
    unknown,
    {pageId: number; ops: EditOp[]}
  >(
    async ({pageId, ops}) => {
      const r = await ChessRestClient.post(
        `${base(uuid)}/edit/`,
        {page_id: pageId, ops},
        {headers: await authHeader()},
      );
      return r.data as EditResult;
    },
    {
      onSuccess: (result, {pageId}) =>
        applyEditResult(client, uuid, pageId, result, 'editor'),
    },
  );

  const undo = useMutation<
    EditResult,
    unknown,
    {pageId?: number; batchId?: string}
  >(
    async ({pageId, batchId}) => {
      const body: Record<string, unknown> = {};
      if (pageId != null) body.page_id = pageId;
      if (batchId) body.batch_id = batchId;
      const r = await ChessRestClient.post(`${base(uuid)}/undo/`, body, {
        headers: await authHeader(),
      });
      return r.data as EditResult;
    },
    {
      onSuccess: (result, {pageId}) =>
        applyEditResult(client, uuid, pageId, result, 'editor'),
    },
  );

  const send = useMutation<
    ChatReply,
    unknown,
    {pageId: number; message: string; context: ChatContext},
    {pendingId: string}
  >(
    async ({pageId, message, context}) => {
      const r = await ChessRestClient.post(
        `${base(uuid)}/page/${pageId}/chat/`,
        {message, context},
        // A turn can take up to a minute (as many as three model calls), so
        // the request is given 90s before it is called a failure.
        {headers: await authHeader(), timeout: CHAT_TIMEOUT_MS},
      );
      return r.data as ChatReply;
    },
    {
      // The reviewer's words appear at once; the server's copy replaces them.
      onMutate: ({pageId, message}) => {
        const pendingId = `pending-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 7)}`;
        client.setQueryData<ChatMessage[]>(chatKey(uuid, pageId), old => [
          ...(old ?? []),
          {
            id: pendingId,
            role: 'user',
            text: message,
            batch_id: null,
            labels: [],
            created_at: new Date().toISOString(),
            pending: true,
          },
        ]);
        return {pendingId};
      },
      onSuccess: (reply, {pageId}, ctx) => {
        client.setQueryData<ChatMessage[]>(chatKey(uuid, pageId), old => [
          ...(old ?? []).filter(m => m.id !== ctx?.pendingId),
          ...(reply?.messages ?? []),
        ]);
        if (reply?.edit)
          applyEditResult(client, uuid, pageId, reply.edit, 'chat');
      },
      onError: (_e, {pageId}, ctx) => {
        client.setQueryData<ChatMessage[]>(chatKey(uuid, pageId), old =>
          (old ?? []).filter(m => m.id !== ctx?.pendingId),
        );
      },
    },
  );

  return {edit, undo, send};
}
